import {MaterialReactTable} from 'material-react-table'
import type {Stores} from '../../../../util/store'
import {useAdminMaterialReactTable} from '../../../../components/table/mui-table'
import {Checkbox} from '@mui/material'
import {useState} from 'react'
import {useManufacturersAliases, WOManufacturerMatchStore,} from './wo-manufacturer-match-store'
import {observer} from 'mobx-react-lite'
import {useParams} from 'react-router-dom'
import {ManufacturerModal} from '../../../models/model-manufacturer-step'
import {useModal} from '../../../../components/modal'
import {RegisteredManufacturerCell} from './registered-manufacturer-cell'
import {ReviewedByCell} from './component/reviewed-by-cell'
import {formatUserName} from '../../../../util/format'
import styles from './index.module.scss'
import {ManufacturerAliasModalState} from './types'
import {TableTotalsLine} from '../../../../components/table/table-totals-line'

export interface WOManufacturerMatchPageOptions {
    stores: Stores
}

export const OrganizationWOManufacturerMatchPage = observer(
    ({ stores }: WOManufacturerMatchPageOptions) => {
        const { organizationId } = useParams()
        const [store] = useState(
            () => new WOManufacturerMatchStore(organizationId!),
        )

        const aliases = useManufacturersAliases(store)
        const { isFetched, total } = store
        const manufacturersModal = useModal<ManufacturerAliasModalState>()

        const table = useAdminMaterialReactTable(
            {
                columns: [
                    {
                        accessorKey: 'input_manufacturer',
                        header: 'Entered Manufacturer',
                        Cell: ({ cell }) => {
                            const inputId = cell.getValue<string>()
                            return <>{inputId}</>
                        },
                    },
                    {
                        accessorKey: 'product_manufacturer_id',
                        header: 'Registered Manufacturer',
                        Cell: ({ row }) => {
                            return (
                                <RegisteredManufacturerCell
                                    row={row}
                                    stores={stores}
                                    onOpen={manufacturersModal.open}
                                />
                            )
                        },
                        filterFn: (row, id, value) => {
                            const manufacturerId = row.getValue<string | null>(
                                id,
                            )

                            if (manufacturerId) {
                                const manufacturer =
                                    stores.manufacturers.manufacturersById[
                                        manufacturerId
                                    ]
                                return manufacturer.name
                                    .toLowerCase()
                                    .includes(value.toLowerCase())
                            }

                            return false
                        },
                    },
                    {
                        accessorKey: 'is_reviewed',
                        header: 'Reviewed',
                        Cell: ({ row }) => {
                            return (
                                <Checkbox
                                    checked={!!row.original.reviewed_by_user_id}
                                    onChange={(_, checked) => {
                                        store.upsertByInputManufacturer(
                                            row.original.input_manufacturer,
                                            {
                                                product_manufacturer_id:
                                                    row.original
                                                        .product_manufacturer_id ??
                                                    null,
                                                is_reviewed: checked,
                                            },
                                        )
                                    }}
                                />
                            )
                        },
                        enableColumnFilter: false,
                    },
                    {
                        accessorKey: 'reviewed_by_user_id',
                        header: 'Reviewed by',
                        Cell: ({ row }) => {
                            return (
                                <ReviewedByCell
                                    userId={row.original.reviewed_by_user_id}
                                    stores={stores}
                                />
                            )
                        },
                        filterFn: (row, id, value) => {
                            const userId = row.getValue<string | null>(id)

                            if (!userId) {
                                return false
                            }

                            const user = stores.users.usersById[userId]

                            if (user) {
                                const str = formatUserName(user) ?? user.id
                                return str
                                    .toLowerCase()
                                    .includes(value.toLowerCase())
                            }

                            return false
                        },
                    },
                    {
                        accessorKey: 'work_order_segments_count',
                        header: 'Segments count',
                        Cell: ({ row }) => {
                            return row.original.work_order_segments_count
                        },
                        enableColumnFilter: false,
                    },
                ],
                data: aliases,
                initialState: {
                    sorting: [
                        {
                            id: 'work_order_segments_count',
                            desc: true,
                        },
                        {
                            id: 'input_manufacturer',
                            desc: false,
                        },
                    ],
                },
                defaultColumn: {
                    enableColumnFilter: true,
                    enableSorting: true,
                },
                getRowId: (row) =>
                    row.alias_id
                        ? `alias:${row.alias_id}`
                        : `ref:${row.input_manufacturer}`,
                enableSorting: true,
                createDisplayMode: 'row',
                enableRowSelection: false,
                state: {
                    isLoading: false,
                    showProgressBars: false,
                },
                renderTopToolbarCustomActions: ({ table }) =>
                    isFetched ? (
                        <TableTotalsLine
                            table={table}
                            getValue={(row) => row.work_order_segments_count}
                            labels={{
                                rows: [
                                    'entered manufacturer',
                                    'entered manufacturers',
                                ],
                                sum: ['segment', 'segments'],
                            }}
                            available={total}
                        />
                    ) : null,
                isPrimaryBackgroundColor: false,
            },
            'manufacturers-table-v2',
        )

        return (
            <div className={styles.container}>
                <MaterialReactTable table={table} />

                {manufacturersModal.state && (
                    <ManufacturerModal
                        stores={stores}
                        anchor={manufacturersModal.state.anchor}
                        onClose={manufacturersModal.close}
                        placement="bottom-start"
                        className={styles.modalContent}
                        overlayClassName={styles.modalOverlay}
                        selectedIds={
                            manufacturersModal.state.data!
                                .initial_manufacturer_id
                                ? new Set([
                                      manufacturersModal.state.data!
                                          .initial_manufacturer_id,
                                  ])
                                : undefined
                        }
                        onManufacturerIdChange={(id) => {
                            const state = manufacturersModal.state?.data

                            if (state) {
                                store.upsertByInputManufacturer(
                                    state.input_manufacturer,
                                    {
                                        product_manufacturer_id: id,
                                        is_reviewed:
                                            !!store.aliasesByInputManufacturer[
                                                state.input_manufacturer
                                            ]?.reviewed_by_user_id,
                                    },
                                )
                                manufacturersModal.close()
                            }
                        }}
                    />
                )}
            </div>
        )
    },
)
